import React, { useEffect, useState } from "react";
import cx from "classnames";
import { Helmet } from "react-helmet";
import { RenderModules } from "src/utils/renderModules";
import { useLocation } from "@reach/router";
import ProductHero from "src/containers/ProductHero";
import SecondaryNav from "src/components/SecondaryNav";
import { ProductSchema } from "src/components/product/schema";
import RedirectModal from "src/components/RedirectModal";

import { useSetPage, useDiscount, useCart } from "src/context/siteContext";
import SEO from "src/components/SEO";
import { Container } from "./styles";
import AnnouncementBarProduct from "src/components/AnnouncementBarProduct";
import { ANBPDesktop } from "src/containers/ProductHero/styles";
import { lazyLoadVideos } from "src/utils/lazyLoad";
import applyDiscount from "src/utils/applyDiscount";
import { trackViewedProductKlaviyo } from "src/utils/viewedProductKlaviyo";

export interface ProductProps {
  pageContext: any;
  preview?: boolean;
}

export interface PackType {
  key: string;
  quantity: number;
  price: string;
}

const Product = ({ pageContext, preview }: ProductProps) => {
  const setPage = useSetPage();
  const location = useLocation();
  const [activePack, setActivePack] = useState<PackType | undefined>();
  const [quantity, setQuantity] = useState(1 as number);
  const [tempQuantity, setTempQuantity] = useState<number | undefined>(1);
  const { addDiscount } = useDiscount();
  const { checkout } = useCart();
  const id = checkout?.id;

  useEffect(() => {
    applyDiscount(id, addDiscount, checkout?.lineItems?.length);
  }, [id]);

  useEffect(() => {
    (window as any).dataLayer.push({ event: "optimize.activate" });
  }, []);

  useEffect(() => {
    if (!activePack) return;

    setQuantity(activePack.quantity);
  }, [activePack]);

  const removeQuantity = () => {
    if (quantity === 1) return;

    removeProductPack();
    setQuantity(quantity - 1);
  };

  const removeQuantityBundle = () => {
    if (quantity === 0) {
      setQuantity(0)
      setTempQuantity(undefined)
      setActivePack(undefined)
    } else {
      removeProductPack();
      setQuantity(quantity - 1);
    }

  }

  const addQuantity = () => {
    if (activePack && quantity == 4) return;
    removeProductPack();
    setQuantity(quantity + 1);
  };

  const setProductPack = (pack: PackType) => {
    if (activePack && pack.key === activePack.key) {
      removeProductPack();
      return;
    }

    setTempQuantity(quantity);

    setActivePack({
      ...pack,
    });
  };

  const removeProductPack = () => {
    setActivePack(undefined);

    if (tempQuantity) {
      setQuantity(tempQuantity);
      setTempQuantity(undefined);
    } else {
      setQuantity(quantity - 1);
    }
  };

  useEffect(() => {
    setPage(pageContext.main.slug);
    trackViewedProductKlaviyo(pageContext)
  }, []);

  useEffect(() => {
      lazyLoadVideos();
  }, [])

  const url = `/products/${pageContext.slug}`;


  return (
    <Container
      notificationBar={
        pageContext.notificationBar
          ? pageContext.notificationBar[0].enabled
          : false
      }
    >
      {/* <AnnouncementBar
        className="showInDesktop"
        firstSVG={returnsSVG}
        firstText={"100 DAYS RETURNS"}
        firstLink={"Moneyback Guarantee"}
        secondSVG={truckTickMarkSVG}
        secondText={`FREE SHIPPING On all orders over ${
          process.env.GATSBY_CURRENCY_SYMBOL == "£" ? "£50" : "$75"
        }`}
      /> */}

      <ANBPDesktop id="var-a">
        <AnnouncementBarProduct
          data={pageContext.main.announcementBarProduct}
        />
      </ANBPDesktop>

      {
        <Helmet>
          <script>
            {`
            window.meta = {
              product: {
                gid: 'gid://shopify/Product/${pageContext.content.shopify.productId
              }',
                id: ${pageContext.content.shopify.productId},
                type: '${pageContext._type}',
                name: '${pageContext.content.shopify.title}',
                brand: 'Sleep Easy',
                price: ${pageContext.content.shopify.defaultPrice},
                imageUrL: '${pageContext.main.mainImage?.image?.url}',
                url: '${process.env.GATSBY_URL + location.pathname}',
                variants: [${pageContext.content.shopify.variants?.map(
                (variant) => `{
                    id: '${variant.content.shopify.variantId}',
                    name: '${variant.content.shopify.title} - ${variant.content.shopify.variantTitle}',
                    price: ${variant.content.shopify.price},
                    public_title: '${variant.content.shopify.variantTitle}',
                    sku: '${variant.content.shopify.sku}',
                  }`
              )}],
                vendor: "Sleep Easy",
              }
            }
          `}
          </script>
        </Helmet>
      }
      <div
        dangerouslySetInnerHTML={{
          __html: `
            <script type="application/ld+json">
              ${JSON.stringify(
            ProductSchema(
              pageContext.main,
              pageContext.content.shopify,
              pageContext?.main?.okendoReviews, 
              pageContext.meta
            )
          )}
            </script>
          `,
        }}
      />
      <SEO
        metaInfo={pageContext?.meta}
        pagePath={url}
        globalMeta={pageContext.siteGlobal?.metaInformation}
      />
      <RedirectModal data={pageContext?.selectCountry} />
      {pageContext.main.nav && pageContext.main.nav?.enabled === true && (
        <SecondaryNav
          title={pageContext.main.pdpTitle}
          id={pageContext?._id}
          mainImage={pageContext.main.mainImage}
          slug={pageContext.main.slug}
          nav={pageContext.main.nav.navList}
          activePack={activePack}
          quantity={quantity}
          defaultPrice={pageContext.content.shopify.defaultPrice}
          productId={pageContext.content.shopify.productId}
          notificationBar={
            pageContext.notificationBar
              ? pageContext.notificationBar[0].enabled
              : false
          }
          mobilePhoto={
            pageContext.main.mobileGallery?.length > 0 &&
            pageContext.main.mobileGallery[0]
          }
          atcButton={pageContext.main.atcButton}
          upsells={pageContext.main.productUpsells}
        />
      )}
      <div className={cx(pageContext.main.slug.current)}>
        <ProductHero
          main={pageContext.main}
          product={pageContext.content.shopify}
          setProductPack={setProductPack}
          removeProductPack={removeProductPack}
          activePack={activePack}
          setActivePack={setActivePack}
          quantity={quantity}
          addQuantity={addQuantity}
          removeQuantity={removeQuantity}
          removeQuantityBundle={removeQuantityBundle}
          setQuantity={setQuantity}
          rating={pageContext.reviews?.rating}
          totalReviews={pageContext.reviews?.total}
          notificationBar={
            pageContext.notificationBar
              ? pageContext.notificationBar[0].enabled
              : false
          }
          upsells={pageContext.main.productUpsells}
          upsellReviews={pageContext.upsellsReviews}
          type={pageContext?._type}
        />
        {RenderModules(pageContext.modules, pageContext)}
      </div>
    </Container>
  );
};

export default Product;
